/**
 * スコア一覧（学生・教育者・管理者向け）
 * 権限に応じて表示内容が変わる
 */

import { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { getScores } from '../lib/scores-api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Loader2 } from 'lucide-react';

export default function ScoresList() {
  const [page, setPage] = useState(1);

  const [, setLocation] = useLocation();
  const { data, isLoading, error } = useQuery({
    queryKey: ['scores', page],
    queryFn: () => getScores({ page, limit: 20 }),
    retry: (_, err) => (err as Error)?.message?.includes('401') ? false : true,
  });

  useEffect(() => {
    if (error && (error as Error)?.message?.includes('401')) {
      setLocation('/login');
    }
  }, [error, setLocation]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">スコア一覧</h1>
        <p className="text-gray-600 mt-2">
          あなたの権限に応じて表示されるスコアデータ
        </p>
      </div>

      {data?.statistics && (
        <Card>
          <CardHeader>
            <CardTitle>統計情報</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="text-sm text-gray-600">平均スコア</div>
                <div className="text-2xl font-bold">{data.statistics.avg?.toFixed(1) || '0.0'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">最小スコア</div>
                <div className="text-2xl font-bold">{data.statistics.min ?? '0'}</div>
              </div>
              <div>
                <div className="text-sm text-gray-600">最大スコア</div>
                <div className="text-2xl font-bold">{data.statistics.max ?? '0'}</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>スコア一覧</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-medical-blue" />
            </div>
          ) : error ? (
            <div className="text-red-600">
              {(error as Error).message || 'エラーが発生しました'}
            </div>
          ) : !data?.scores?.length ? (
            <div className="text-gray-500 py-8 text-center">
              スコアデータがありません。面接を終了するとスコアが記録されます。
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ユーザー</TableHead>
                    <TableHead>面接ID</TableHead>
                    <TableHead>患者スコア</TableHead>
                    <TableHead>歯科医スコア</TableHead>
                    <TableHead>作成日時</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.scores.map((row) => (
                    <TableRow
                      key={row.interview_id}
                      className="cursor-pointer hover:bg-gray-50"
                      onClick={() => {
                        const url = `/evaluation?interview_id=${row.interview_id}`;
                        console.log("[ScoresList] 評価画面へ遷移:", url, "interview_id=", row.interview_id);
                        window.location.href = url;
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          const url = `/evaluation?interview_id=${row.interview_id}`;
                          console.log("[ScoresList] 評価画面へ遷移(キー):", url);
                          window.location.href = url;
                        }
                      }}
                    >
                      <TableCell>
                        <div>
                          <div className="font-medium">{row.user_name || '-'}</div>
                          <div className="text-sm text-gray-500">{row.user_email || '-'}</div>
                        </div>
                      </TableCell>
                      <TableCell>{row.interview_id}</TableCell>
                      <TableCell className="font-bold">
                        {row.patient_score_value != null ? row.patient_score_value : '-'}
                      </TableCell>
                      <TableCell className="font-bold">
                        {row.dentist_score_value != null ? row.dentist_score_value : '-'}
                      </TableCell>
                      <TableCell>
                        {row.created
                          ? new Date(row.created).toLocaleString('ja-JP')
                          : '-'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {data.pagination && data.pagination.total > 0 && (
                <div className="flex justify-between items-center mt-4">
                  <div className="text-sm text-gray-600">
                    全 {data.pagination.total} 件中{' '}
                    {(page - 1) * 20 + 1} -{' '}
                    {Math.min(page * 20, data.pagination.total)} 件を表示
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page - 1)}
                      disabled={page === 1}
                    >
                      前へ
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(page + 1)}
                      disabled={page * 20 >= data.pagination.total}
                    >
                      次へ
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

    </div>
  );
}
